import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderContext } from '../../../../contexts/LoaderContext';
import { useMap, useMapEvents, Marker, Polyline } from 'react-leaflet';
import { LatLng, LeafletMouseEvent } from 'leaflet';
import L from 'leaflet';
import { MarsApiConfig as apiConfig } from '../../../../api/mars-api-config';
import { MarsApiService as api } from '../../../../api/mars-api-service';
import images from '../../../../assets/images';
import { mapColors } from '../../../../types/map';
import { MeasuringToolSelectPoints } from '../../../../types/measuring-tool';
import { DistanceCalculationResponse } from '../../../../types/distance-calculation-info';

const MeasuringToolControl: React.FC<MeasuringToolSelectPoints> = ({
  isMeasuringActive,
  onPointsSelected,
}) => {
  const { t } = useTranslation();
  const map = useMap();
  const { setIsLoading } = useLoaderContext();
  const [measurePoints, setMeasurePoints] = useState<LatLng[]>([]);
  const [distance, setDistance] = useState<string | null>(null);
  const tooltipRef = useRef<L.Tooltip | null>(null);
  const popupRef = useRef<L.Popup | null>(null);

  const MapTooltip = () => {
    useMapEvents({
      mousemove(e: LeafletMouseEvent) {
        if (!isMeasuringActive || measurePoints.length >= 2) return;
        if (!tooltipRef.current) {
          tooltipRef.current = L.tooltip({
            permanent: false,
            direction: 'auto',
            className: 'm-l-tooltip-wrapper',
          });
        }
        const message =
          measurePoints.length === 0
            ? t('tooltips.selectFirstPoint')
            : t('tooltips.selectSecondPoint');
        tooltipRef.current.setContent(message).setLatLng(e.latlng).openOn(map);
      },
      click(e: LeafletMouseEvent) {
        if (!isMeasuringActive || measurePoints.length >= 2) return;
        const target = e.originalEvent.target as HTMLElement;
        if (target.closest('.measuring-tool-button')) return;

        const newPoints = [...measurePoints, e.latlng];
        setMeasurePoints(newPoints);

        if (newPoints.length === 2) {
          if (tooltipRef.current) {
            map.closeTooltip(tooltipRef.current);
            tooltipRef.current = null;
          }
          const payload = {
            fromLat: +newPoints[0].lat.toFixed(4),
            fromLon: +newPoints[0].lng.toFixed(4),
            toLat: +newPoints[1].lat.toFixed(4),
            toLon: +newPoints[1].lng.toFixed(4),
          };
          api
            .post(
              {
                url: apiConfig.measureDistanceBetweenPoints,
                data: payload,
              },
              setIsLoading
            )
            .then((res: DistanceCalculationResponse) => {
              const distanceResult = `${res} ${'NM'}`;
              setDistance(distanceResult);
            })
            .catch((error) => {
              console.error('API error:', error);
              setDistance(null);
              onPointsSelected?.({
                point1: newPoints[0],
                point2: newPoints[1],
                distance: null,
              });
            });
        }
      },
    });
    return null;
  };

  useEffect(() => {
    if (measurePoints.length === 2 && distance && !popupRef.current) {
      const textWidth = distance.length * 12;
      const maxWidth = Math.max(150, textWidth + 20);
      popupRef.current = L.popup({
        autoPan: false,
        minWidth: 50,
        maxWidth: maxWidth,
        className: 'm-l-tooltip-wrapper',
      })
        .setLatLng(L.latLngBounds(measurePoints).getCenter())
        .setContent(`<div class="m-l-tooltip-wrapper">${distance}</div>`)
        .openOn(map);
    }
  }, [measurePoints, distance, map]);

  useEffect(() => {
    if (!isMeasuringActive) {
      setMeasurePoints([]);
      setDistance(null);
      if (tooltipRef.current) {
        map.closeTooltip(tooltipRef.current);
        tooltipRef.current = null;
      }
      if (popupRef.current) {
        map.closePopup(popupRef.current);
        popupRef.current = null;
      }
    }
  }, [isMeasuringActive, map]);

  return (
    <>
      {isMeasuringActive &&
        measurePoints.map((point, index) => (
          <Marker
            key={`measure-point-${index}`}
            position={point}
            icon={L.icon({
              iconUrl: index === 0 ? images.redCircle : images.orangeCircle,
              iconSize: [12, 12],
              iconAnchor: [6, 6],
            })}
          />
        ))}
      {measurePoints.length === 2 && (
        <Polyline
          positions={measurePoints}
          color={mapColors.darkBlue}
          weight={4}
        />
      )}
      <MapTooltip />
    </>
  );
};

export default MeasuringToolControl;
